// src/services/api.js
import axios from 'axios';

/* ------------------------------------------------------------------ */
/* 0.  Base instance & helpers                                         */
/* ------------------------------------------------------------------ */

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';


const storage = {
  async getItem(key) {
    return Promise.resolve(localStorage.getItem(key));
  },
  async setItem(key, val) {
    localStorage.setItem(key, val);
  },
  async multiSet(pairs) {
    pairs.forEach(([k, v]) => localStorage.setItem(k, v));
  },
  async multiRemove(keys) {
    keys.forEach((k) => localStorage.removeItem(k));
  },
};

export const api = axios.create({
  baseURL: `${API_BASE_URL}/admin`, // all admin endpoints are prefixed
});

// Attach bearer before every request
api.interceptors.request.use(async (config) => {
  const token = await storage.getItem('@authToken');
  if (token) {
    config.headers = { ...(config.headers ?? {}), Authorization: token };
  }
  return config;
});

/* -------- token refresh when we get 401 / 422 --------------------- */
let isRefreshing = false;
let queue = /** @type {Array<(t: string) => void>} */ ([]);

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const { config, response } = err;
    if (!response || ![401, 422].includes(response.status) || config._retry) {
      return Promise.reject(err);
    }

    /* single-flight refresh */
    if (isRefreshing) {
      return new Promise((resolve) => {
        queue.push((newTok) => {
          config.headers.Authorization = newTok;
          resolve(api(config));
        });
      });
    }

    isRefreshing = true;
    config._retry = true;

    try {
      const refreshTok = await storage.getItem('@refreshToken');
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${refreshTok}` } }
      );

      const bearer = `Bearer ${data.access_token}`;
      await storage.setItem('@authToken', bearer);

      queue.forEach((cb) => cb(bearer));
      queue = [];
      isRefreshing = false;

      config.headers.Authorization = bearer;
      return api(config);
    } catch (refreshError) {
      queue = [];
      isRefreshing = false;
      await storage.multiRemove([
        '@authToken',
        '@refreshToken',
        '@establishmentId',
      ]);
      // TODO: navigate to admin sign-in screen here
      return Promise.reject(refreshError);
    }
  }
);

/* ------------------------------------------------------------------ */
/* 1.  Authentication helpers                                          */
/* ------------------------------------------------------------------ */

/**
 * Establishment-level admin login (same endpoint valet uses)
 * returns { establishment_id, access_token, refresh_token }
 */
export async function loginEstablishmentAdmin(establishmentId, passcode) {
  const { data } = await axios.post(`${API_BASE_URL}/establishment/login`, {
    establishment_id: establishmentId,
    passcode,
  });

  await storage.multiSet([
    ['@authToken', `Bearer ${data.access_token}`],
    ['@refreshToken', data.refresh_token],
    ['@establishmentId', String(establishmentId)],
  ]);

  return { establishmentId: data.establishment_id };
}

/**
 * Super-admin login (optional)
 */
export async function loginSuperAdmin(email, password) {
  const { data } = await axios.post(`${API_BASE_URL}/super_admin/login`, {
    email,
    password,
  });
  await storage.multiSet([
    ['@authToken', `Bearer ${data.access_token}`],
    ['@refreshToken', data.refresh_token],
    ['@superAdminId', String(data.user_id)],
  ]);
  return { userId: data.user_id, role: data.role };
}

export async function logoutAdmin() {
  await storage.multiRemove([
    '@authToken',
    '@refreshToken',
    '@establishmentId',
    '@superAdminId',
  ]);
}

/* ------------------------------------------------------------------ */
/* 2.  Dashboard                                                       */
/* ------------------------------------------------------------------ */

export async function getDashboardSummary() {
  const { data } = await api.get('/dashboard/summary');
  return data; /* { total_valets, total_rides, active_rides, active_valets } */
}

export async function getDashboardActivity({ period, start_date, end_date }) {
  const { data } = await api.get('/dashboard/activity', {
    params: { period, start_date, end_date },
  });
  return data; /* { period, buckets: [...] } */
}

export async function getPeakHours({ period, day_of_week }) {
  const { data } = await api.get('/dashboard/peak_hours', {
    params: { period, day_of_week },
  });
  return data; /* hourly_average array */
}

/* ------------------------------------------------------------------ */
/* 3.  Valet management                                                */
/* ------------------------------------------------------------------ */

export async function listValets(params = {}) {
  const { data } = await api.get('/valets', { params });
  return data; // { valets, page, per_page, total }
}

export async function createValet({ name }) {
  const { data } = await api.post('/valets', { name });
  return data; // { id, message }
}

export async function updateValet(valetId, payload) {
  await api.put(`/valets/${valetId}`, payload);
}

export async function deleteValet(valetId) {
  await api.delete(`/valets/${valetId}`);
}

export async function getValetEfficiency({ top_n = 10, period = '30_days' }) {
  const { data } = await api.get('/valet_efficiency', {
    params: { top_n, period },
  });
  return data; // { period, top_n, results: [...] }
}

/* ------------------------------------------------------------------ */
/* 4.  Ride management                                                 */
/* ------------------------------------------------------------------ */

export async function listRides(filters = {}) {
  const { data } = await api.get('/rides', { params: filters });
  return data; // { rides, page, per_page, total }
}

export async function getRide(rideId) {
  const { data } = await api.get(`/rides/${rideId}`);
  return data;
}

export async function createManualRide(payload) {
  const { data } = await api.post('/rides/manual', payload);
  return data; // { ride_id, message }
}

export async function updateRideStatus(rideId, payload) {
  await api.put(`/rides/${rideId}/status`, payload);
}

export async function deleteRide(rideId) {
  await api.delete(`/rides/${rideId}`);
}

/* ------------------------------------------------------------------ */
/* 5.  Card management                                                 */
/* ------------------------------------------------------------------ */

export async function listCards(params = {}) {
  const { data } = await api.get('/cards', { params });
  return data; // { cards, page, per_page, total }
}

export async function createCard(payload) {
  await api.post('/cards', payload); /* { message } */
}

export async function updateCard(cardId, payload) {
  await api.put(`/cards/${cardId}`, payload);
}

export async function deleteCard(cardId) {
  await api.delete(`/cards/${cardId}`);
}

/* ------------------------------------------------------------------ */
/* 6.  Establishment info & settings                                   */
/* ------------------------------------------------------------------ */

export async function getEstablishmentInfo() {
  const { data } = await api.get('/establishment/info');
  return data; // { id, name, created_at }
}

/**
 * Change passcode (old + new)
 * @param {{ old_password: string; new_password: string }} payload
 */
export async function changeEstablishmentPassword(payload) {
  const { data } = await api.put('/establishment/password', payload);
  return data; // { message }
}

/* ------------------------------------------------------------------ */
/* 7.  Non-admin ride actions (same backend, establishment JWT)        */
/* ------------------------------------------------------------------ */

/**
 * Create a ride (check-in) as the establishment.
 * Payload: { card_id, valet_id, parking_space?, car_details? }
 */
export async function createRide(payload) {
  const token = await storage.getItem('@authToken');
  const { data } = await axios.post(`${API_BASE_URL}/ride`, payload, {
    headers: { Authorization: token },
  });
  return data; // { message, ride_id }
}

/**
 * Mark ride as "intent_pickup" (start retrieval).
 * Optionally include { pickup_valet_id }.
 */
export async function markIntentPickup(rideId, payload = {}) {
  const token = await storage.getItem('@authToken');
  await axios.put(`${API_BASE_URL}/ride/${rideId}/intent_pickup`, payload, {
    headers: { Authorization: token },
  });
}

/**
 * Finish a ride (checkout).
 */
export async function finishRide(rideId) {
  const token = await storage.getItem('@authToken');
  await axios.put(`${API_BASE_URL}/ride/${rideId}/finish`, {}, {
    headers: { Authorization: token },
  });
}

/**
 * Lookup an active ride by card (establishment-scoped).
 * Returns { ride_id, status, check_in_time, check_out_time, qrData } or { ride_id:null, status:'none' }
 */
export async function getActiveRideByCard(cardId, establishmentId) {
  const token = await storage.getItem('@authToken');
  const { data } = await axios.get(`${API_BASE_URL}/ride/by_card`, {
    headers: { Authorization: token },
    params: { cardId, establishmentId },
  });
  return data;
}


/* ------------------------------------------------------------------ */
/* Export default (optional)                                           */
/* ------------------------------------------------------------------ */

const apiExport =  {
  /* auth */
  loginEstablishmentAdmin,
  loginSuperAdmin,
  logoutAdmin,
  /* dashboard */
  getDashboardSummary,
  getDashboardActivity,
  getPeakHours,
  /* valets */
  listValets,
  createValet,
  updateValet,
  deleteValet,
  getValetEfficiency,
  /* rides */
  listRides,
  getRide,
  createManualRide,
  updateRideStatus,
  deleteRide,
  /* cards */
  listCards,
  createCard,
  updateCard,
  deleteCard,
  /* establishment */
  getEstablishmentInfo,
  changeEstablishmentPassword,
  /* non-admin ride actions */
    createRide,
  markIntentPickup,
  finishRide,
  getActiveRideByCard,
};

export default apiExport;